<template>
    <div class="c-people-listing">

        <div class="c-people-listing__header-bar">
            <div class="c-people-listing__header-bar-layout">
                <div class="c-people-listing__header-bar-split">
                    <h1 class="c-people-listing__header-bar-title">
                        {{title}}
                    </h1>
                    <div class="c-people-listing__search">
                        <span class="c-people-listing__search-button iconf-search"></span>
                        <input class="c-people-listing__search-input" name="search" type="text" v-model="searchText" placeholder="Search our people" />
                    </div>
                </div>
                <div class="c-people-listing__header-bar-split">

                </div>
            </div>
        </div>

        <div class="c-filters-bar c-people-listing__filter-bar">
            <div class="c-filters__panel c-people-listing__filters" id="js-filters" v-if="peopleFilters">
                <button type="button"
                        data-flyout-toggle="filter"
                        class="c-filters-toggler mobile-only"
                        aria-label="Show Filters">
                    Show Filters
                </button>

                <filters :filters="peopleFilters"
                         :filter-is-showing="filtersActive"
                         @filters-changed="onFiltersChanged"
                         @toggle-filters="filtersActive = !filtersActive" />
            </div>
        </div>
        <div class="c-people-listing__wrapper">
            <div class="c-people-listing__wrapper-layout">

                <div class="c-people-listing__count c-people-listing__count--top">
                    {{ displayCount }} - Sorted by: {{ sortedBy === 'position' ? 'Position' : 'Name (A-Z)' }}

                    <button type="button" class="c-people-listing__sort-toggle"
                        @click="sortedBy = sortedBy === 'position' ? 'alpha' : 'position'; fetchPeople()">
                        Sort by: {{ sortedBy === 'position' ? 'Name (A-Z)' : 'Position' }}
                    </button>
                </div>

                <TransitionGroup class="c-people-listing__container" name="list" tag="div" mode="out-in" appear>
                    <person-card :item="item" v-for="item in paginatedListing" :key="item.id"></person-card>
                </TransitionGroup>

                <div class="c-people-listing__count c-people-listing__count--bottom">
                    {{ displayCount }}
                </div>

                <pagination v-if="totalItems > pageSize"
                            :total-items="totalItems"
                            :page-size="pageSize"
                            :current-page="currentPage"
                            @page-changed="onPageChanged" />

            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, watch, onMounted } from 'vue';

    // Reactive state
    const peopleList = ref([]);
    const searchText = ref('');
    const title = ref(null);
    const loaded = ref(false);
    const peopleFilters = ref(null);
    const selectedFilters = ref([]);
    const filtersTouched = ref(false);
    const filtersActive = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(12);
    const totalItems = ref(0);
    const totalPages = ref(0);
    const apiUrl = ref(null);
    const isLoading = ref(false);
    const sortedBy = ref('position');

    // Computed properties
    const selectedFiltersByGroup = computed(() => {
        const map = {};

        if (!peopleFilters.value || !peopleFilters.value.filters) return map;

        peopleFilters.value.filters.forEach(filter => {
            const selected = (filter.filterOptions || []).filter(opt => opt.selected).map(opt => opt.id);
            if (selected.length) {
                map[filter.name] = selected;
            }
        });

        return map;
    });

    const displayCount = computed(() => {
        if (totalItems.value === 0) {
            return 'No people found';
        }

        const start = ((currentPage.value - 1) * pageSize.value) + 1;
        const end = Math.min(currentPage.value * pageSize.value, totalItems.value);

        return `Displaying ${start}-${end} of ${totalItems.value}`;
    });

    const filteredListing = computed(() => {
        // With server-side pagination, items are already filtered
        return peopleList.value || [];
    });

    const paginatedListing = computed(() => {
        // With server-side pagination, items are already paginated
        return filteredListing.value;
    });

    // Methods
    const fetchPeople = () => {
        if (!apiUrl.value || isLoading.value) return;

        isLoading.value = true;

        const url = new URL(apiUrl.value);
        url.searchParams.set('page', currentPage.value);

        if (searchText.value) {
            url.searchParams.set('term', searchText.value);
        }

        // Add filter parameters to API request (supports multiple selections)
        const selectedByGroup = selectedFiltersByGroup.value;

        if (selectedByGroup['Position'] && selectedByGroup['Position'].length > 0) {
            // Send all selected position IDs
            selectedByGroup['Position'].forEach(id => {
                url.searchParams.append('position', id);
            });
        }

        if (selectedByGroup['Location'] && selectedByGroup['Location'].length > 0) {
            // Send all selected location IDs
            selectedByGroup['Location'].forEach(id => {
                url.searchParams.append('location', id);
            });
        }

        if (sortedBy.value) {
            url.searchParams.set('sort', sortedBy.value);
        }

        fetch(url).then(res => res.json()).then(response => {
            peopleList.value = response.items || [];
            peopleFilters.value = response.filters || {};
            pageSize.value = response.pagination?.pageSize || 12;
            totalItems.value = response.pagination?.totalItems || 0;
            totalPages.value = response.pagination?.totalPages || 0;
            currentPage.value = response.pagination?.currentPage || 1;

            loaded.value = true;

            // Restore filters from localStorage (only on first load)
            if (!filtersTouched.value) {
                const savedFilters = JSON.parse(localStorage.getItem('people-listing-filters') || '[]');

                if (savedFilters.length && peopleFilters.value && peopleFilters.value.filters) {
                    peopleFilters.value.filters.forEach(filter => {
                        (filter.filterOptions || []).forEach(option => {
                            option.selected = savedFilters.includes(option.id);
                        });
                    });
                    selectedFilters.value = savedFilters;
                    filtersTouched.value = true;
                }
            } else {
                // Reapply selected state to filter options after fetch
                if (selectedFilters.value.length && peopleFilters.value && peopleFilters.value.filters) {
                    peopleFilters.value.filters.forEach(filter => {
                        (filter.filterOptions || []).forEach(option => {
                            option.selected = selectedFilters.value.includes(option.id);
                        });
                    });
                }
            }

            isLoading.value = false;
        }).catch(() => {
            peopleList.value = [];
            isLoading.value = false;
        });
    };

    const onFiltersChanged = (selected) => {
        selectedFilters.value = selected;
        filtersTouched.value = true;
        currentPage.value = 1; // Reset to first page on filter change

        localStorage.setItem('people-listing-filters', JSON.stringify(selected));
        fetchPeople();
    };

    const onPageChanged = (page) => {
        currentPage.value = page;
        fetchPeople();

        const offset = 100; // Adjust for sticky header height
        const elementPosition = document.querySelector('.c-people-listing').getBoundingClientRect().top + window.pageYOffset;

        window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    };

    // Watchers
    watch(searchText, () => {
        currentPage.value = 1; // Reset to first page on search
        fetchPeople();
    });

    // Lifecycle hooks
    onMounted(() => {
        const baseUrl = window.location.origin;

        const DataProp = document.querySelector('.c-people-listing').parentElement;
        const DataAPI = DataProp?.getAttribute('data-people-api');
        const DataTitle = DataProp?.getAttribute('data-title');

        title.value = DataTitle;
        apiUrl.value = baseUrl + DataAPI;

        fetchPeople();
    });
</script>